import type {
    AllowanceAction,
    AllowanceResponse,
    AllowanceState,
    LimitState,
    StateRefusal,
} from "@spectacle/chat-view-spec";
import { utcDayToPretty } from "@/util/date.ts";

/**
 * The chat allowance, as the app shows it.
 *
 * Every state and number here is decided by the chat service
 * (`resolveAllowance`, chat/src/allowance.ts) -- the same function that
 * refuses a turn -- so the meter and a refusal cannot disagree. This module
 * only words them. It never works out a state from the plan or a percentage.
 *
 * The contract is @spectacle/chat-view-spec's allowance.ts. Its types are
 * imported; the four values below are mirrored, because app's dependency on
 * that package must stay type-only (design-conventions.test.ts says why).
 * Each is typed against the package, so a missing key fails to compile, and
 * allowance.test.ts checks every value against the package's own.
 */

export const ALLOWANCE_STATES = [
    "ok",
    "low",
    "used",
    "trialUsed",
    "off",
    "paused",
] as const satisfies readonly AllowanceState[];

export const ALLOWANCE_ACTIONS = [
    "addPaymentMethod",
    "viewPlans",
    "contactUs",
] as const satisfies readonly AllowanceAction[];

/** The refusals that come with a `data-error` part, and the state each means. */
export const REFUSAL_STATE = {
    allowanceUsed: "used",
    trialAllowanceUsed: "trialUsed",
    chatOff: "off",
    servicePaused: "paused",
} as const satisfies Record<StateRefusal, LimitState>;

/**
 * What each refusal says, word for word: the stream's error, and the
 * headline of the limit. The date is kept out of it so the sentence can be
 * matched exactly; `limitResetSentence` adds it.
 */
export const LIMIT_SENTENCE: Record<LimitState, string> = {
    used: "Your team has used this month's chat allowance.",
    trialUsed: "You've used the chat included in your trial.",
    off: "Chat is turned off for your organization.",
    paused: "Chat is unavailable right now. We've been alerted.",
};

const isOneOf = <T extends string>(
    values: readonly T[],
    value: unknown,
): value is T => values.includes(value as T);

const isResetsAt = (value: unknown): value is string | null =>
    value === null ||
    (typeof value === "string" && !Number.isNaN(Date.parse(value)));

const isAllowanceAction = (value: unknown): value is AllowanceAction | null =>
    value === null || isOneOf(ALLOWANCE_ACTIONS, value);

const isStateRefusal = (value: unknown): value is StateRefusal =>
    typeof value === "string" && Object.hasOwn(REFUSAL_STATE, value);

/** A `GET /chat/allowance` body the app can trust, or false. */
export function isAllowanceResponse(
    value: unknown,
): value is AllowanceResponse {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as Record<string, unknown>;

    return (
        isOneOf(ALLOWANCE_STATES, candidate.state) &&
        typeof candidate.usedPercent === "number" &&
        isResetsAt(candidate.resetsAt) &&
        isAllowanceAction(candidate.action)
    );
}

/**
 * One read of the allowance, and when it was asked for.
 *
 * The time the request STARTED, not when it answered: a read sent before a
 * turn was refused can land after the refusal and still describe the month
 * before that turn's spend. Only a read started after the refusal may lift
 * it.
 */
export type ChatAllowanceRead = {
    allowance: AllowanceResponse;
    /** Epoch milliseconds. */
    requestedAt: number;
};

/**
 * Chat has stopped, and why: from the allowance read, or from a turn the
 * service just refused.
 */
export type Limit = {
    state: LimitState;
    resetsAt: string | null;
    action: AllowanceAction | null;
};

/**
 * The refusal a `data-error` part describes and the limit it means, or
 * undefined for a part that is not one of the four the service sends.
 */
export function refusalFromErrorPart(
    data: unknown,
): { reason: StateRefusal; limit: Limit } | undefined {
    if (typeof data !== "object" || data === null) return undefined;
    const part = data as Record<string, unknown>;
    if (!isStateRefusal(part.code)) return undefined;

    return {
        reason: part.code,
        limit: {
            state: REFUSAL_STATE[part.code],
            resetsAt: isResetsAt(part.resetsAt) ? part.resetsAt : null,
            action: isAllowanceAction(part.action) ? part.action : null,
        },
    };
}

const LIMIT_STATES: readonly AllowanceState[] = Object.values(REFUSAL_STATE);

export function isLimitState(state: AllowanceState): state is LimitState {
    return LIMIT_STATES.includes(state);
}

/**
 * What stops chat now. A refusal from this session wins over the allowance
 * read: it is the newer word on the matter, and the read is refetched after
 * it anyway.
 */
export function currentLimit(
    allowance: AllowanceResponse | undefined,
    refused: Limit | undefined,
): Limit | undefined {
    if (refused) return refused;
    if (!allowance || !isLimitState(allowance.state)) return undefined;

    return {
        state: allowance.state,
        resetsAt: allowance.resetsAt,
        action: allowance.action,
    };
}

/** "It resets on 1 October.", or null when there is no date to give. */
export const resetSentence = (resetsAt: string | null): string | null =>
    resetsAt ? `It resets on ${utcDayToPretty(resetsAt)}.` : null;

/**
 * "It resets on 1 October.", or null. Only a spent allowance resets on a
 * date: off and paused have none, and this does not trust a date sent with
 * them. A trial ending before the 1st has none either, and the service says
 * so with a null.
 */
export function limitResetSentence(limit: Limit): string | null {
    return limit.state === "used" || limit.state === "trialUsed"
        ? resetSentence(limit.resetsAt)
        : null;
}

export function limitSentence(limit: Limit): string {
    const resets = limitResetSentence(limit);
    return resets
        ? `${LIMIT_SENTENCE[limit.state]} ${resets}`
        : LIMIT_SENTENCE[limit.state];
}

/** The notice at 80%. */
export function lowSentence(allowance: AllowanceResponse): string {
    const used = `Your team has used ${allowance.usedPercent}% of this month's chat allowance.`;
    const resets = resetSentence(allowance.resetsAt);
    return resets ? `${used} ${resets}` : used;
}

/**
 * Whether there is a percentage worth drawing. Off and paused are not a
 * reading of how much is left: chat is not running at all.
 */
export function showsMeter(state: AllowanceState): boolean {
    return state !== "off" && state !== "paused";
}

/** The meter's reading, and its screen-reader label. */
export function meterLabel(allowance: AllowanceResponse): string {
    return `Chat allowance: ${allowance.usedPercent}% used this month`;
}

export const ACTION_LABEL: Record<AllowanceAction, string> = {
    addPaymentMethod: "Add a payment method",
    viewPlans: "View plans",
    contactUs: "Contact us",
};

/**
 * What a member reads where an admin gets the action. Members cannot add a
 * card or change a plan, and "Contact us" from a member reaches us about a
 * decision that is their admin's.
 *
 * "Off" asks the admin to contact us because that is all an admin can do
 * about it: organizations have no switch of their own, and we turn chat off
 * or back on with `chat:set-allowance` on request.
 */
export function memberHint(state: AllowanceState): string {
    return state === "off"
        ? "Ask an admin to contact us."
        : "Ask an admin to upgrade.";
}

const LIMIT_SENTENCES: readonly string[] = Object.values(LIMIT_SENTENCE);

/**
 * Whether a turn's error is one of the limit refusals. The limit itself says
 * it, with the date and the action, while it stands; once a newer read lifts
 * it, the old sentence would contradict an open composer.
 */
export function isLimitSentence(message: string): boolean {
    return LIMIT_SENTENCES.includes(message);
}

/** The first line of the support conversation "Contact us" opens. */
export function supportMessage(state: AllowanceState): string {
    switch (state) {
        case "off":
            return "Hi, chat is turned off for our organization and we'd like to use it.";
        case "trialUsed":
            return "Hi, I've used the chat included in my trial.";
        case "low":
            return "Hi, our team is close to this month's chat allowance.";
        default:
            return "Hi, our team has used this month's chat allowance.";
    }
}
